import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import type { ReactElement } from 'react';
import type { CustomerDTO } from '../../../server/dto/customer-dto.ts';
import { api } from '../../../utils/api.ts';
import { ActivityFilterBar } from '../../activity-feed-new/components/activity-filter-bar.tsx';
import type { ActivityFilterPreset } from '../../activity-feed-new/components/activity-filter-presets.tsx';
import { CustomerActivityFeed } from '../../activity-feed-new/components/customer-activity-feed.tsx';
import { useStickyBarRef } from '../../activity-feed-new/hooks/use-sticky-bar-ref.ts';

export type NewCustomerActivityFeedProps = {
    customerBasics: CustomerDTO;
};

/**
 * The reworked customer activity feed, behind `enableNewCustomerDetailView`:
 * the new filter bar over the section-card feed. Pinned notes are dropped for good.
 */
export function NewCustomerActivityFeed({ customerBasics }: NewCustomerActivityFeedProps): ReactElement {
    const stickyBarRef = useStickyBarRef();
    const { canFilterQuoteOrderByStatus, canReadQuotes, canReadOpenOrders } = useAppConfig();

    // The same counts the old page's stats bar showed; the page already fetches them.
    const { data: openTransactionSummary } =
        api.platoUser.getDetailCustomer.getOpenTransactionSummary.useQuery(
            { erpCustomerId: customerBasics.customerId },
            { enabled: canFilterQuoteOrderByStatus },
        );

    const presets: ActivityFilterPreset[] = [
        ...(canReadQuotes
            ? [{ preset: 'openQuotes' as const, count: openTransactionSummary?.openQuoteCount }]
            : []),
        ...(canReadOpenOrders
            ? [{ preset: 'openOrders' as const, count: openTransactionSummary?.openOrderCount }]
            : []),
    ];

    return (
        // stacked under the overview, the search needs more air above so it reads with the feed
        <section className="flex flex-1 flex-col gap-section max-lg:mt-default [--section-card-sticky-gap:0px]">
            <ActivityFilterBar
                ref={stickyBarRef}
                scope="customer"
                showUserFilter
                presets={canFilterQuoteOrderByStatus && presets.length > 0 ? presets : undefined}
                className="sticky top-0 z-10 -my-cozy bg-wash py-cozy"
            />
            <CustomerActivityFeed customerBasics={customerBasics} />
        </section>
    );
}
